import React, { type FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { Icon, IconSize } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import { closeLiveStream, selectStreamWithAutoplay, setPipMode } from 'app/modules/LiveStream/actions';
import { selectLiveStreamPreferences } from 'app/modules/LiveStream/selectors';
import { openLoginDialog } from 'app/modules/Signing/actions';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import dictionary from './LiveStreamIcon.dictionary.json';

import styles from './LiveStreamIcon.module.scss';

type LiveStreamIconProps = {
  matchId: number;
  dataGaEvent: string;
  title?: string;
  className?: string;
  size?: IconSize;
};

export const LiveStreamIcon: FC<LiveStreamIconProps> = ({
  matchId,
  dataGaEvent,
  title,
  className,
  size = IconSize.MEDIUM,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { closed, streamId } = useSelector(selectLiveStreamPreferences);
  const isStreamRunning = (streamId === matchId) && !closed;
  const positiveAction = isLoggedIn
    ? t(dictionary.liveStreamOn)
    : t(dictionary.loginAndLiveStreamOn);
  const defaultTitle = isStreamRunning ? t(dictionary.liveStreamOff) : positiveAction;

  const wrapperClassNames = classNames(styles.root, className);

  const handleStreamStart = useCallback(() => {
    if (isLoggedIn) {
      dispatch(selectStreamWithAutoplay(matchId));
    }
    else {
      dispatch(openLoginDialog());
    }
  }, [isLoggedIn]);

  const handleStreamClose = useCallback(() => {
    dispatch(closeLiveStream());
    dispatch(setPipMode(false));
  }, []);

  return (
    <button type="button"
            title={title || defaultTitle}
            className={wrapperClassNames}
            onClick={isStreamRunning ? handleStreamClose : handleStreamStart}
            data-ga-event={dataGaEvent}
            data-ga-sport-match={matchId}>
      <Icon icon="broadcast"
            size={size} />
    </button>
  );
};
